/// Renders the optional road / town overlay GeoJSON. Two passes:
///   - Polygon / MultiPolygon → very faint translucent fills for
///     town and urban-area blobs. iOS uses `SKColor(white: 1.0,
///     alpha: 0.06)` plus a slightly stronger stroke (alpha 0.10).
///   - LineString / MultiLineString → thin neutral strokes for
///     roads, rails, motorways. iOS uses alpha 0.18.
///
/// The data set is large (~2 MB on N51) but visually quiet — the
/// goal is "you can tell where built-up land is and which roads
/// cross it" without competing with the gameplay layers.
///
/// Visual rules ported from FlatoutATC/Support/GameScene.swift —
/// `drawOverlay()` (iOS line range ~2668–2752).

import { Container, Graphics } from 'pixi.js'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type {
  FeatureCollection,
  LineString,
  MultiLineString,
  MultiPolygon,
  Polygon,
  Position,
} from '../types/GeoJSON'

const OVERLAY_COLOR = 0xffffff
const POLY_FILL_ALPHA = 0.06
const POLY_STROKE_ALPHA = 0.1
const LINE_ALPHA = 0.18
const POLY_STROKE_WIDTH = 0.4
const LINE_STROKE_WIDTH = 0.5

export class OverlayLayer extends Container {
  constructor(features: FeatureCollection, projector: CoordinateProjector) {
    super()
    const fills = new Graphics()
    const lines = new Graphics()
    this.addChild(fills)
    this.addChild(lines)

    for (const feat of features.features) {
      if (!feat.geometry) continue
      const g = feat.geometry
      switch (g.type) {
        case 'Polygon':
          drawPolygon(fills, (g as Polygon).coordinates, projector)
          break
        case 'MultiPolygon':
          for (const poly of (g as MultiPolygon).coordinates) {
            drawPolygon(fills, poly, projector)
          }
          break
        case 'LineString':
          drawLine(lines, (g as LineString).coordinates, projector)
          break
        case 'MultiLineString':
          for (const line of (g as MultiLineString).coordinates) {
            drawLine(lines, line, projector)
          }
          break
        default:
          break
      }
    }

    lines.stroke({
      width: LINE_STROKE_WIDTH,
      color: OVERLAY_COLOR,
      alpha: LINE_ALPHA,
    })
  }
}

function drawPolygon(
  g: Graphics,
  rings: Position[][],
  projector: CoordinateProjector,
): void {
  const outer = rings[0]
  if (!outer || outer.length < 3) return
  const points: number[] = []
  for (const [lon, lat] of outer) {
    const p = projector.project({ latitude: lat, longitude: lon }, true)
    points.push(p.x, p.y)
  }
  g.poly(points)
  g.fill({ color: OVERLAY_COLOR, alpha: POLY_FILL_ALPHA })
  g.stroke({
    width: POLY_STROKE_WIDTH,
    color: OVERLAY_COLOR,
    alpha: POLY_STROKE_ALPHA,
  })
}

function drawLine(
  g: Graphics,
  line: Position[],
  projector: CoordinateProjector,
): void {
  if (line.length < 2) return
  let started = false
  for (const pos of line) {
    const [lon, lat] = pos
    const p = projector.project({ latitude: lat, longitude: lon }, true)
    if (!started) {
      g.moveTo(p.x, p.y)
      started = true
    } else {
      g.lineTo(p.x, p.y)
    }
  }
}
